import { useState } from 'react';
import { Table, Switch, Button, Space, Tag, Tooltip, App, Typography, theme, type TableColumnsType } from 'antd';
import { PlusOutlined, DeleteOutlined, BookOutlined, LockOutlined, TranslationOutlined, FileTextOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useGlossary, useUpdateTerm, useDeleteTerm } from '@/hooks/useGlossary';
import { getGlossaryStats } from '@/api/glossaryStats';
import { AddTermModal } from './AddTermModal';
import { ExpandedTermRow } from './ExpandedTermRow';
import { ImportCsvButton } from './ImportCsvButton';
import { ExportCsvButton } from './ExportCsvButton';
import { EmptyState } from '@/components/EmptyState';
import { PageHero, HeroChip } from '@/components/PageHero';
import { KpiTile } from '@/components/KpiTile';
import { SectionCard } from '@/components/SectionCard';
import type { GlossaryTerm } from '@/types/glossary';

const { Text } = Typography;

export function GlossaryListPage() {
  const { message, modal } = App.useApp();
  const { token } = theme.useToken();
  const q = useGlossary();
  const update = useUpdateTerm();
  const del = useDeleteTerm();
  const statsQ = useQuery({ queryKey: ['glossary-stats'], queryFn: getGlossaryStats });
  const stats = statsQ.data;
  const [addOpen, setAddOpen] = useState(false);

  async function togglePreserve(t: GlossaryTerm, next: boolean) {
    try {
      await update.mutateAsync({ id: t.id, input: { preserve_as_is: next } });
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  function confirmDelete(t: GlossaryTerm) {
    modal.confirm({
      title: `Delete glossary term "${t.term}"?`,
      content: 'This removes all per-language translations for this term.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await del.mutateAsync(t.id);
          message.success('Deleted');
        } catch (e) {
          message.error((e as Error).message);
        }
      },
    });
  }

  const columns: TableColumnsType<GlossaryTerm> = [
    { title: 'Term', dataIndex: 'term', width: 220, render: (v) => <code>{v}</code> },
    { title: 'Preserve as-is', dataIndex: 'preserve_as_is', width: 140, render: (v: boolean, t) => <Switch checked={v} onChange={(next) => togglePreserve(t, next)} /> },
    { title: 'Notes', dataIndex: 'notes', ellipsis: true, render: (v) => (v ? <Text type="secondary">{v}</Text> : <Text type="secondary">—</Text>) },
    {
      title: 'Translations', width: 320,
      render: (_, t) => {
        if (t.preserve_as_is) return <Tag color="blue">preserved</Tag>;
        if (t.translations.length === 0) return <Text type="secondary">none yet</Text>;
        const shown = t.translations.slice(0, 3);
        const more = t.translations.length - shown.length;
        return (
          <Space size={4}>
            {shown.map((tr) => <Tag key={tr.lang_code}>{tr.lang_code}: {tr.translation}</Tag>)}
            {more > 0 && <Tag>+{more}</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Actions', width: 90,
      render: (_, t) => (
        <Tooltip title="Delete"><Button danger size="small" icon={<DeleteOutlined />} onClick={() => confirmDelete(t)} /></Tooltip>
      ),
    },
  ];

  const maxLang = Math.max(1, ...(stats?.by_language ?? []).map((b) => b.count));
  const Bar = ({ label, count }: { label: string; count: number }) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ color: token.colorTextSecondary }}>{count}</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: token.colorFillSecondary }}>
        <div style={{ height: 8, borderRadius: 4, width: `${Math.round((count / maxLang) * 100)}%`, background: '#f59e0b' }} />
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1100 }}>
      <PageHero
        icon={<BookOutlined />}
        title="Glossary"
        subtitle="Preserved terms and steered translations"
        from="#d97706" to="#f59e0b"
        chips={[
          <HeroChip key="terms">Terms {stats?.total_terms ?? 0}</HeroChip>,
          <HeroChip key="preserved">Preserved {stats?.preserved ?? 0}</HeroChip>,
        ]}
        actions={
          <Space>
            <ImportCsvButton />
            <ExportCsvButton terms={q.data ?? []} />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Add Term</Button>
          </Space>
        }
      />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <KpiTile tone="sky" icon={<BookOutlined />} label="Total terms" value={stats?.total_terms ?? 0} />
        <KpiTile tone="slate" icon={<LockOutlined />} label="Preserved" value={stats?.preserved ?? 0} />
        <KpiTile tone="green" icon={<TranslationOutlined />} label="Translatable" value={stats?.translatable ?? 0} />
        <KpiTile tone="amber" icon={<FileTextOutlined />} label="Translations" value={stats?.total_translations ?? 0} />
      </div>

      <SectionCard tone="amber" icon={<TranslationOutlined />} title="Translations per language" desc="Glossary translations by language">
        {(stats?.by_language ?? []).map((b) => <Bar key={b.code} label={`${b.name} (${b.code})`} count={b.count} />)}
      </SectionCard>

      <SectionCard tone="amber" icon={<BookOutlined />} title="All terms" desc="Preserve, translate, edit">
        <Table
          rowKey="id"
          loading={q.isLoading}
          columns={columns}
          dataSource={q.data ?? []}
          pagination={{ pageSize: 50 }}
          expandable={{
            expandedRowRender: (t) => <ExpandedTermRow term={t} />,
            rowExpandable: (t) => !t.preserve_as_is,
          }}
          locale={{
            emptyText: (
              <EmptyState title="No glossary terms yet" description="Add terms to preserve brand names or steer translations across languages." ctaLabel="Add Term" onCta={() => setAddOpen(true)} />
            ),
          }}
        />
      </SectionCard>

      <AddTermModal open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}
